<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useToggle } from '@vueuse/core';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Button from 'dashboard/components-next/button/Button.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import UnifiedCampaignCard from 'dashboard/components-next/Campaigns/Pages/AllCampaigns/UnifiedCampaignCard.vue';
import CampaignFilters from 'dashboard/components-next/Campaigns/Pages/AllCampaigns/CampaignFilters.vue';
import CampaignCreateDialog from 'dashboard/components-next/Campaigns/Pages/AllCampaigns/CampaignCreateDialog.vue';
import SMSCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/SMSCampaign/SMSCampaignDialog.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import SendTestCampaignDialog from 'dashboard/components-next/Campaigns/Pages/AllCampaigns/SendTestCampaignDialog.vue';
import {
  CAMPAIGN_CHANNEL_FILTERS,
  CAMPAIGN_STATUS_FILTERS,
  channelFromRouteParam,
  filterCampaigns,
} from 'dashboard/components-next/Campaigns/helpers/campaignFilters';
import { testPageUrlFor } from 'dashboard/components-next/Campaigns/helpers/campaignBuilder';
import {
  buildCreateChannels,
  buildScratchDraftPayload,
  buildTemplateDraftPayload,
} from 'dashboard/components-next/Campaigns/helpers/campaignGallery';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

// The channel view is the route, not local state: `campaigns_index` is All and
// `campaigns_channel_index` carries the channel in its path, so a channel view
// survives a reload and can be linked to.
const activeChannel = computed(() =>
  channelFromRouteParam(route.params.channel)
);

const statusFilter = ref(CAMPAIGN_STATUS_FILTERS.ALL);
const searchQuery = ref('');

const selectedCampaign = ref(null);
// The WhatsApp campaign the dialog is editing; null means it is creating one.
const editingWhatsAppCampaign = ref(null);
// The gallery template a new WhatsApp campaign starts from; null for a blank one.
const whatsAppStarterTemplate = ref(null);
// Same for SMS, whose form prefills its message from the template.
const smsStarterTemplate = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);
const sendTestCampaignDialogRef = ref(null);

const [showCreateDialog, toggleCreateDialog] = useToggle();
const [showSMSCampaignDialog, toggleSMSCampaignDialog] = useToggle();
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

const isCreatingDraft = ref(false);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const allCampaigns = useMapGetter('campaigns/getAllCampaigns');
const websiteInboxes = useMapGetter('inboxes/getWebsiteInboxes');
const smsInboxes = useMapGetter('inboxes/getSMSInboxes');
const whatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isWhatsAppEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.WHATSAPP_CAMPAIGNS
  )
);

// Which channels exist and whether each is usable is pure logic in
// `buildCreateChannels`; only the copy is added here, where `t` lives.
const createChannels = computed(() =>
  buildCreateChannels({
    hasWebsiteInbox: websiteInboxes.value.length > 0,
    hasSmsInbox: smsInboxes.value.length > 0,
    hasWhatsAppInbox: whatsAppInboxes.value.length > 0,
    isWhatsAppEnabled: isWhatsAppEnabled.value,
  }).map(channel => {
    const copyRoot = `CAMPAIGN.GALLERY.CHANNEL_STEP.${channel.value.toUpperCase()}`;
    return {
      ...channel,
      label: t(`CAMPAIGN.GALLERY.CHANNELS.${channel.value.toUpperCase()}`),
      description: t(`${copyRoot}.DESCRIPTION`),
      unavailableReason: channel.isAvailable
        ? ''
        : t(`${copyRoot}.UNAVAILABLE`),
    };
  })
);

// Newest first: a freshly created or duplicated campaign surfaces at the top.
const sortedCampaigns = computed(() =>
  [...allCampaigns.value].sort((a1, a2) => a2.id - a1.id)
);

// Everything the current view covers, before the status chips and the search
// narrow it - so an empty channel shows its own empty state rather than
// claiming the filters hid something.
const channelCampaigns = computed(() =>
  filterCampaigns(sortedCampaigns.value, { channel: activeChannel.value })
);

const filteredCampaigns = computed(() =>
  filterCampaigns(channelCampaigns.value, {
    status: statusFilter.value,
    searchQuery: searchQuery.value,
  })
);

const hasNoCampaigns = computed(
  () => channelCampaigns.value.length === 0 && !isFetchingCampaigns.value
);

const hasNoFilterMatches = computed(
  () =>
    !isFetchingCampaigns.value &&
    channelCampaigns.value.length > 0 &&
    filteredCampaigns.value.length === 0
);

const clearFilters = () => {
  statusFilter.value = CAMPAIGN_STATUS_FILTERS.ALL;
  searchQuery.value = '';
};

/**
 * Per-channel copy and create action. The channel views reuse the legacy
 * per-channel page copy, which already says the right thing, and the create
 * button drops straight into that channel's flow instead of the picker.
 */
const CHANNEL_VIEWS = {
  [CAMPAIGN_CHANNEL_FILTERS.ALL]: { copyRoot: 'CAMPAIGN.ALL', key: 'ALL' },
  [CAMPAIGN_CHANNEL_FILTERS.LIVE_CHAT]: {
    copyRoot: 'CAMPAIGN.LIVE_CHAT',
    key: 'LIVE_CHAT',
  },
  [CAMPAIGN_CHANNEL_FILTERS.SMS]: { copyRoot: 'CAMPAIGN.SMS', key: 'SMS' },
  [CAMPAIGN_CHANNEL_FILTERS.WHATSAPP]: {
    copyRoot: 'CAMPAIGN.WHATSAPP',
    key: 'WHATSAPP',
  },
};

const channelView = computed(() => CHANNEL_VIEWS[activeChannel.value]);

const headerTitle = computed(() =>
  t(`${channelView.value.copyRoot}.HEADER_TITLE`)
);
const headerSubtitle = computed(() =>
  t(`${channelView.value.copyRoot}.HEADER_SUBTITLE`)
);
const emptyStateTitle = computed(() =>
  t(`${channelView.value.copyRoot}.EMPTY_STATE.TITLE`)
);
const emptyStateSubtitle = computed(() =>
  t(`${channelView.value.copyRoot}.EMPTY_STATE.SUBTITLE`)
);
const createLabel = computed(() =>
  t(`CAMPAIGN.ALL.CREATE_FOR_CHANNEL.${channelView.value.key}`)
);

// Creates the draft the gallery asked for and drops straight into the
// builder. The channel step will not offer live chat without a website inbox,
// so the first one is read directly — the builder's targeting tab is where the
// inbox gets changed afterwards.
const createDraft = async payloadForInbox => {
  isCreatingDraft.value = true;
  try {
    const campaign = await store.dispatch(
      'campaigns/create',
      payloadForInbox(websiteInboxes.value[0].id)
    );
    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONGOING,
    });
    toggleCreateDialog(false);
    router.push({
      name: 'campaigns_builder',
      params: { accountId: accountId.value, displayId: campaign.id },
    });
  } catch (error) {
    useAlert(t('CAMPAIGN.GALLERY.CREATE_ERROR'));
  } finally {
    isCreatingDraft.value = false;
  }
};

const handleSelectTemplate = template =>
  createDraft(inboxId => buildTemplateDraftPayload({ template, inboxId }));

const handleStartFromScratch = () =>
  createDraft(inboxId =>
    buildScratchDraftPayload({ title: t('CAMPAIGN.GALLERY.UNTITLED'), inboxId })
  );

const openWhatsAppCampaignDialog = (
  campaign = null,
  starterTemplate = null
) => {
  editingWhatsAppCampaign.value = campaign;
  whatsAppStarterTemplate.value = starterTemplate;
  toggleWhatsAppCampaignDialog(true);
};

const closeWhatsAppCampaignDialog = () => {
  toggleWhatsAppCampaignDialog(false);
  editingWhatsAppCampaign.value = null;
  whatsAppStarterTemplate.value = null;
};

const closeSMSCampaignDialog = () => {
  toggleSMSCampaignDialog(false);
  smsStarterTemplate.value = null;
};

const openSMSCampaignDialog = (starterTemplate = null) => {
  smsStarterTemplate.value = starterTemplate;
  toggleSMSCampaignDialog(true);
};

const handleCreateSMS = () => {
  toggleCreateDialog(false);
  openSMSCampaignDialog();
};

const handleCreateWhatsApp = () => {
  toggleCreateDialog(false);
  openWhatsAppCampaignDialog();
};

// An SMS gallery pick carries the message itself, so it opens the campaign
// dialog with the form prefilled.
const handleSelectSMSTemplate = template => {
  toggleCreateDialog(false);
  openSMSCampaignDialog(template);
};

// A WhatsApp gallery pick cannot become a draft on its own the way a live chat
// one does — the message still has to come from a Meta-approved template — so
// it opens the campaign dialog carrying the template as a starting point.
const handleSelectWhatsAppTemplate = template => {
  toggleCreateDialog(false);
  openWhatsAppCampaignDialog(null, template);
};

const handleToggleEnabled = async campaign => {
  const isPausing = campaign.enabled;
  try {
    await store.dispatch('campaigns/update', {
      id: campaign.id,
      enabled: !campaign.enabled,
    });
    useAlert(
      isPausing
        ? t('CAMPAIGN.ALL.TOGGLE.PAUSED_MESSAGE')
        : t('CAMPAIGN.ALL.TOGGLE.RESUMED_MESSAGE')
    );
  } catch (error) {
    useAlert(t('CAMPAIGN.ALL.TOGGLE.ERROR_MESSAGE'));
  }
};

const handleDuplicate = async campaign => {
  try {
    await store.dispatch('campaigns/duplicate', campaign.id);
    useAlert(t('CAMPAIGN.ALL.DUPLICATE.SUCCESS_MESSAGE'));
  } catch (error) {
    // Surfaces backend validation messages, e.g. the account campaign limit.
    const errorMessage =
      error?.response?.data?.message ||
      t('CAMPAIGN.ALL.DUPLICATE.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// Live chat campaigns open in the full-page builder; WhatsApp ones reopen the
// dialog they were created in, loaded with the campaign. SMS keeps its creation
// dialog for now, so the card does not offer Edit on it.
const handleEdit = campaign => {
  if (campaign.inbox?.channel_type === INBOX_TYPES.WHATSAPP) {
    openWhatsAppCampaignDialog(campaign);
    return;
  }

  router.push({
    name: 'campaigns_builder',
    params: { accountId: accountId.value, displayId: campaign.id },
  });
};

// A live chat campaign is tested by visiting the page it targets; one without a
// URL rule has no page of its own, so the bundled widget harness stands in.
const handleTest = campaign => {
  window.open(testPageUrlFor(campaign), '_blank', 'noopener');
};

const handleSendTest = campaign => {
  selectedCampaign.value = campaign;
  sendTestCampaignDialogRef.value.dialogRef.open();
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

// Every channel now starts in the same dialog; a channel view pre-selects its
// channel there rather than skipping the picker.
const handleCreate = () => toggleCreateDialog(true);
</script>

<template>
  <CampaignLayout
    :header-title="headerTitle"
    :header-subtitle="headerSubtitle"
    :button-label="createLabel"
    @click="handleCreate"
  >
    <template #action>
      <CampaignCreateDialog
        v-if="showCreateDialog"
        :channels="createChannels"
        :initial-channel="activeChannel"
        :is-creating="isCreatingDraft"
        @close="toggleCreateDialog(false)"
        @select-template="handleSelectTemplate"
        @select-sms-template="handleSelectSMSTemplate"
        @select-whatsapp-template="handleSelectWhatsAppTemplate"
        @start-from-scratch="handleStartFromScratch"
        @create-sms="handleCreateSMS"
        @create-whatsapp="handleCreateWhatsApp"
      />
      <SMSCampaignDialog
        v-if="showSMSCampaignDialog"
        :starter-template="smsStarterTemplate"
        @close="closeSMSCampaignDialog"
      />
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        :campaign="editingWhatsAppCampaign"
        :starter-template="whatsAppStarterTemplate"
        @close="closeWhatsAppCampaignDialog"
      />
    </template>

    <div class="flex flex-col gap-5">
      <CampaignFilters
        v-if="!hasNoCampaigns"
        v-model:status="statusFilter"
        v-model:search-query="searchQuery"
      />

      <div
        v-if="isFetchingCampaigns"
        class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
      >
        <Skeleton
          v-for="card in 6"
          :key="card"
          class="h-64"
          :announce="card === 1"
        />
      </div>

      <div
        v-else-if="filteredCampaigns.length"
        class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
      >
        <UnifiedCampaignCard
          v-for="campaign in filteredCampaigns"
          :key="campaign.id"
          :campaign="campaign"
          @toggle-enabled="handleToggleEnabled(campaign)"
          @duplicate="handleDuplicate(campaign)"
          @edit="handleEdit(campaign)"
          @test="handleTest(campaign)"
          @send-test="handleSendTest(campaign)"
          @delete="handleDelete(campaign)"
        />
      </div>

      <EmptyStateLayout
        v-else-if="hasNoCampaigns"
        compact
        icon="i-lucide-megaphone"
        :title="emptyStateTitle"
        :subtitle="emptyStateSubtitle"
      >
        <template #actions>
          <Button
            :label="createLabel"
            icon="i-lucide-plus"
            @click="handleCreate"
          />
        </template>
      </EmptyStateLayout>

      <div
        v-else-if="hasNoFilterMatches"
        class="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center"
      >
        <h3 class="mb-0 text-base font-medium text-n-slate-12">
          {{ t('CAMPAIGN.ALL.FILTERS.NO_MATCH_TITLE') }}
        </h3>
        <p class="max-w-md mb-0 text-sm text-n-slate-11">
          {{ t('CAMPAIGN.ALL.FILTERS.NO_MATCH_SUBTITLE') }}
        </p>
        <Button
          :label="t('CAMPAIGN.ALL.FILTERS.CLEAR')"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-filter-x"
          class="mt-3"
          @click="clearFilters"
        />
      </div>
    </div>

    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />

    <SendTestCampaignDialog
      ref="sendTestCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
